import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { SelectComponent } from '@components/select/select.component'
import { InputComponent } from '@components/input/input.component'
import { CAcademyCardComponent } from '@components/c-academy-card/c-academy-card.component'
import { PaginationComponent } from '@components/pagination/pagination.component'
import { CAcademyFormation } from '../../interfaces/c-academy-formation'
import { SelectValue } from '../../interfaces/select-value.interface'
import { NgForOf, NgIf } from '@angular/common'
import { AnalyticsService } from 'app/services/analytics.service'
import { debounceTime, Subject } from 'rxjs'

@Component({
  selector: 'app-c-academy-list',
  standalone: true,
    imports: [
        TranslateModule,
        SelectComponent,
        InputComponent,
        CAcademyCardComponent,
        PaginationComponent,
        NgIf,
        NgForOf
    ],
  templateUrl: './c-academy-list.component.html',
  styleUrl: './c-academy-list.component.scss'
})
export class CAcademyListComponent implements OnInit{

    @Input() title!: string;
    @Input() formations!: CAcademyFormation[];
    @Input() elementsPerPage: number = 8;
    @Input() shouldShowFilters: boolean = false;
    @Output() onUpdate: EventEmitter<void> = new EventEmitter();

    currentPage: number = 0;
    totalPages: number = 1;
    currentPageFormations: CAcademyFormation[] = [];

    // Filters
    availableTypes: SelectValue[] = [];
    availableBrands: SelectValue[] = [];

    filteredApplication: SelectValue | null = null;
    filteredBrand: SelectValue | null = null;
    search: string = '';

    private searchTerm$ = new Subject<string>();

    constructor(
        private translate: TranslateService,
        private analytics: AnalyticsService
    ) {
    }

    ngOnInit() {
        // Calculate total pages based on the number of formations and elements per page
        if (this.formations && this.formations.length > 0) {
            this.totalPages = Math.ceil(this.formations.length / this.elementsPerPage);
        } else {
            this.totalPages = 1; // Default to 1 if no formations are available
        }
        // Get the current page formations based on the current page and elements per page
        this.setupPage()

        if(this.shouldShowFilters){
            this.getAvailableFormationTypes();
            this.getAvailableFormationBrands();
        }

        this.searchTerm$
            .pipe(debounceTime(800)) // ajusta el tiempo según lo que consideres "pausa"
            .subscribe((term) => {
                const filteredTitles = this.currentPageFormations.map(f => f.title).join('_');

                this.analytics.track({
                    event: 'search',
                    search_term: term,
                    type: filteredTitles || ''
                });
            });
    }

    setupPage() {
        this.currentPageFormations = this.formations.slice(
            this.currentPage * this.elementsPerPage,
            (this.currentPage + 1) * this.elementsPerPage
        );
    }

    getAvailableFormationTypes(){
        const uniqueTypes = [...new Set(
            this.formations
                .filter(formation => formation.application)
                .map(formation => formation.application)
        )]
        this.availableTypes = [{
            id: 'all',
            label: this.translate.instant('all'),
            value: 'all'
        }];
        this.availableTypes = this.availableTypes.concat(uniqueTypes.map(type => ({
            id: <string> type,
            label: this.translate.instant(`cacademy.application.${type}`),
            value: <string> type
        })));
    }

    getAvailableFormationBrands() {
        const uniqueBrands = [...new Set(
            this.formations
                .flatMap(formation => formation.brands)
        )];
        this.availableBrands = [{
            id: 'all',
            label: this.translate.instant('all'),
            value: 'all'
        }]
        this.availableBrands = this.availableBrands.concat(uniqueBrands.map(brand => ({
            id: brand,
            label: this.translate.instant(brand),
            value: brand
        })));
    }

    onPageChange(page: number) {
        this.currentPage = page;
        this.setupPage()
    }


    onApplicationFilter($event: SelectValue | null) {
        this.filteredApplication = $event;
        this.currentPage = 0; // Reset to first page when filter changes

        this.filterResults()

        const filteredTitles = this.currentPageFormations.map(f => f.title).join('_');

        this.analytics.track({
            event: 'click',
            click_type: 'filter',
            click_action: 'application_' + $event?.value,
            type: filteredTitles || ''
        });
    }

    onBrandFilter($event: SelectValue | null) {
        this.filteredBrand = $event;
        this.currentPage = 0; // Reset to first page when filter changes

        this.filterResults()

        const filteredTitles = this.currentPageFormations.map(f => f.title).join('_');

        this.analytics.track({
            event: 'click',
            click_type: 'filter',
            click_action: 'brand_' + $event?.value,
            type: filteredTitles || ''
        });
    }

    filterSearch(search: any, keyPressed: boolean) {
        if(!keyPressed) return;
        this.search = search ? search.toLowerCase() : search;

        this.filterResults()

        if (keyPressed) {
            this.searchTerm$.next(this.search);
        }
    }

    filterResults(){
        const filteredBrand = this.filteredBrand ? this.filteredBrand.value : 'all';
        const filteredType = this.filteredApplication ? this.filteredApplication.value : 'all';
        const search = this.search;

        this.currentPage = 0; // Reset to first page when filter changes

        const filteredFormations = this.formations.filter(formation => {
            const matchesBrand = filteredBrand === 'all' || formation.brands.includes(filteredBrand);
            const matchesType = filteredType === 'all' || formation.application === filteredType;
            const matchesSearch = !this.search || formation.title.toLowerCase().includes(search) || formation.shortDescription.toLowerCase().includes(search) || formation.description.toLowerCase().includes(search);
            return matchesBrand && matchesType && matchesSearch;
        });

        this.totalPages = Math.ceil(filteredFormations.length / this.elementsPerPage);
        this.currentPageFormations = filteredFormations.slice(
            this.currentPage * this.elementsPerPage,
            (this.currentPage + 1) * this.elementsPerPage
        );
    }

    onFormationUpdate(){
        this.onUpdate.emit();
    }

}
